import PropTypes from 'prop-types';
import React, { useCallback, useContext, useState } from 'react';
import '../styles/ProductGallery.css';
import ResponsiveImage from './ResponsiveImage';
import ParallaxModel from './ParallaxModal';
import ZoomableImageWithLens from './ZoomableImageWithLens';
import AppContext from './context/AppContext';
import Product3DModelThumbnail from './Product3DModelThumbnail';

const ProductGallery = ({ images = [], product, models = [] }) => {
    const { appContext } = useContext(AppContext);
    const appConfig = appContext.config.themeConfig;
    const [activeIndex, setActiveIndex] = useState(0);
    const [activeModelIndex, setActiveModelIndex] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [isImage, setIsImage] = useState(true);
    const [isModel, setIsModel] = useState(false);
    // show modal on image Click
    const handleImageClick = useCallback(() => {
        setShowModal(true);
    }, []);

    // Cancel model on click outside
    const cancelModel = useCallback(() => {
        setShowModal(false);
        handleImageSelect(0);
    }, [handleImageSelect]);
    const stopPropagation = useCallback((e) => {
        e.stopPropagation();
    }, []);

    const imagesWithUrl = images.filter(
        (image) => typeof image.url === 'string' && image.url !== '',
    );

    const handleModelSelect = useCallback((index) => {
        setActiveModelIndex(index);
        setIsModel(true);
        setIsImage(false);
        setShowModal(true);
    }, []);

    const handleImageSelect = (index) => {
        setActiveIndex(index);
        setIsImage(true);
        setIsModel(false);
    };

    return <section
        className={'product-gallery--container'}>
        <ParallaxModel
            show={showModal}
            index={activeIndex}
            onClose={cancelModel}
            onClick={stopPropagation}
            productTitle={product.title}
            images={images}
            isImage={isImage}
            isModel={isModel}
            models={models}
            modelIndex={activeModelIndex}
        />
        <div
            className={'product-gallery--viewer'}>
            {imagesWithUrl.length < 1 ? (
                <ZoomableImageWithLens
                    imageUrl={appConfig.productNoImageUrl}
                    imageAlt={'No product image'}
                    showLens={false}
                />
            ) : (
                <ZoomableImageWithLens
                    imageUrl={imagesWithUrl[activeIndex].url}
                    imageAlt={imagesWithUrl[activeIndex].altText || ''}
                    onClick={handleImageClick}
                />
            )}
        </div>
        {imagesWithUrl.length > 0 && <button
            className={'product-gallery--zoom-button'}
            onClick={handleImageClick}
        >
            <svg
                xmlns={'http://www.w3.org/2000/svg'}
                width={'16'}
                height={'16'}
                fill={'currentColor'}
                viewBox={'0 0 16 16'}
            >
                <path
                    d={'M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z'}
                />
                <path
                    d={'M6.5 10.5a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 0 1H7a.5.5 0 0 1-.5-.5z'}
                />
                <path
                    d={'M10.5 7.5a.5.5 0 0 1-.5.5H7a.5.5 0 0 1 0-1h3a.5.5 0 0 1 .5.5z'}
                />
            </svg>
            Click to zoom in
        </button>}
        <div
            className={'product-gallery--navigation'}>
            {imagesWithUrl.map((image, index) => <button
                key={`${index}-${image?.url || ''}`}
                className={`product-gallery--thumbnail-button${activeIndex === index ? ' active' : ''}`}
                onClick={() => handleImageSelect(index)}
            >
                <ResponsiveImage
                    imageUrl={image.url}
                    imageAlt={image.altText || ''}
                    mobileSize={'75px'}
                    desktopSize={'75px'}
                />
            </button>)}
            {models.map((model, index) => <Product3DModelThumbnail
                index={index}
                key={`${index}-${model?.id || ''}`}
                onClick={handleModelSelect}
                model={model} />)}
        </div>
    </section>;
};

ProductGallery.propTypes = {
    product: PropTypes.shape({
        title: PropTypes.string,
        Images: PropTypes.array,
    }),
    images: PropTypes.arrayOf(PropTypes.shape({
        altText: PropTypes.string,
        Attachment: PropTypes.shape({
            localPath: PropTypes.string.isRequired,
        }),
    })),
    models: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        posterUrl: PropTypes.string,
        url: PropTypes.string,
    })),
};

export default ProductGallery;